// Components/ChatComponents/BubbleManagement/BubbleSettingsPanel.jsx
// Comprehensive settings management for bubble hosts/admins
// ✅ UPDATED: Added require_visibility_to_post setting

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, Switch, FormControlLabel,
  Stack, Divider, Alert, CircularProgress, Tab, Tabs,
  Card, CardContent, IconButton, Avatar, Chip
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Image as ImageIcon,
  Palette as PaletteIcon,
  Security as SecurityIcon,
  Info as InfoIcon,
  People as PeopleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import API from '../API';

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
}));

const ColorPicker = styled(Box)(({ theme, color }) => ({
  width: 60,
  height: 60,
  borderRadius: 8,
  border: `2px solid ${theme.palette.divider}`,
  backgroundColor: color,
  cursor: 'pointer',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.05)',
    borderColor: theme.palette.primary.main,
  },
}));

export default function BubbleSettingsPanel({ open, onClose, bubble, onSave }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cover_image: '',
    avatar_image: '',
    theme_colors: {
      primary: '#1976d2',
      secondary: '#9c27b0',
    },
    participant_visibility: true,
    allow_participant_posts: true,
    allow_participant_organizers: false,
    require_visibility_to_post: false, // ✅ NEW
  });

  // Initialize form data when bubble loads
  useEffect(() => {
    if (bubble) {
      setFormData({
        name: bubble.name || '',
        description: bubble.description || '',
        cover_image: bubble.cover_image || '',
        avatar_image: bubble.avatar_image || '',
        theme_colors: bubble.theme_colors || {
          primary: '#1976d2',
          secondary: '#9c27b0',
        },
        participant_visibility: bubble.participant_visibility ?? true,
        allow_participant_posts: bubble.allow_participant_posts ?? true,
        allow_participant_organizers: bubble.allow_participant_organizers ?? false,
        require_visibility_to_post: bubble.require_visibility_to_post ?? false, // ✅ NEW
      });
    }
  }, [bubble]);

  const updateSettingsMutation = useMutation(
    (data) => API.bubbles.updateSettings(bubble.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['bubble-metadata', bubble.id]);
        queryClient.invalidateQueries(['bubble_messages', bubble.conversation_id]);
        onSave?.();
        onClose();
      },
      onError: (err) => {
        setError(err?.response?.data?.error || 'Failed to update settings');
      },
    }
  );

  const handleSave = () => {
    setError(null);
    updateSettingsMutation.mutate(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // ✅ NEW: Auto-disable require_visibility_to_post if allow_participant_posts is disabled
      if (field === 'allow_participant_posts' && !value) {
        updated.require_visibility_to_post = false;
      }
      
      return updated;
    });
  };

  const handleColorChange = (colorType, value) => {
    setFormData(prev => ({
      ...prev,
      theme_colors: {
        ...prev.theme_colors,
        [colorType]: value
      }
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            Bubble Settings
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <StyledTab icon={<InfoIcon />} iconPosition="start" label="General" />
          <StyledTab icon={<PaletteIcon />} iconPosition="start" label="Appearance" />
          <StyledTab icon={<SecurityIcon />} iconPosition="start" label="Permissions" />
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* GENERAL TAB */}
        {activeTab === 0 && (
          <Stack spacing={3}>
            <TextField
              label="Bubble Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              required
              helperText="The name of your bubble community"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              rows={4}
              helperText="Tell members what this bubble is about"
            />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Current Stats
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Chip
                  icon={<PeopleIcon />}
                  label={`${bubble.member_count} members`}
                  color="primary"
                />
                <Chip
                  label={bubble.is_public ? 'Public' : 'Private'}
                  color={bubble.is_public ? 'success' : 'default'}
                />
                {bubble.is_paid && (
                  <Chip
                    label={`$${bubble.access_price}${bubble.is_subscription ? `/${bubble.subscription_interval}` : ''}`}
                    color="warning"
                  />
                )}
              </Box>
            </Box>
          </Stack>
        )}

        {/* APPEARANCE TAB */}
        {activeTab === 1 && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Avatar Image
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={formData.avatar_image}
                  sx={{ width: 80, height: 80 }}
                />
                <TextField
                  label="Avatar Image URL"
                  value={formData.avatar_image}
                  onChange={(e) => handleChange('avatar_image', e.target.value)}
                  fullWidth
                  placeholder="https://..."
                  helperText="URL to your bubble's avatar image"
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Cover Image
              </Typography>
              <TextField
                label="Cover Image URL"
                value={formData.cover_image}
                onChange={(e) => handleChange('cover_image', e.target.value)}
                fullWidth
                placeholder="https://..."
                helperText="URL to your bubble's cover image"
              />
              {formData.cover_image && (
                <Box
                  sx={{
                    mt: 2,
                    width: '100%',
                    height: 200,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'divider'
                  }}
                >
                  <img
                    src={formData.cover_image}
                    alt="Cover preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Theme Colors
              </Typography>
              <Box display="flex" gap={3} mt={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Primary Color
                  </Typography>
                  <ColorPicker
                    color={formData.theme_colors.primary}
                    onClick={() => {
                      const color = prompt('Enter hex color:', formData.theme_colors.primary);
                      if (color) handleColorChange('primary', color);
                    }}
                  />
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {formData.theme_colors.primary}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Secondary Color
                  </Typography>
                  <ColorPicker
                    color={formData.theme_colors.secondary}
                    onClick={() => {
                      const color = prompt('Enter hex color:', formData.theme_colors.secondary);
                      if (color) handleColorChange('secondary', color);
                    }}
                  />
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {formData.theme_colors.secondary}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>
        )}

        {/* PERMISSIONS TAB */}
        {activeTab === 2 && (
          <Stack spacing={3}>
            <Alert severity="info" icon={<SecurityIcon />}>
              Control what members can do in your bubble
            </Alert>

            <Card variant="outlined">
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.participant_visibility}
                      onChange={(e) => handleChange('participant_visibility', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Participant Visibility
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        When enabled, members can see who else is in the bubble. When disabled, members are hidden from each other (only you can see all members).
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allow_participant_posts}
                      onChange={(e) => handleChange('allow_participant_posts', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Allow Participant Posts
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Members can create posts and contribute to the feed. Disable to make this bubble read-only for members.
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>

            {/* ✅ NEW: Require Visibility to Post Setting */}
            <Card 
              variant="outlined"
              sx={{
                opacity: formData.allow_participant_posts ? 1 : 0.5,
                transition: 'opacity 0.2s'
              }}
            >
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.require_visibility_to_post}
                      onChange={(e) => handleChange('require_visibility_to_post', e.target.checked)}
                      disabled={!formData.allow_participant_posts}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Require Visibility to Post
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        When enabled, members must be visible to other participants to create posts. This prevents anonymous/hidden posting.
                      </Typography>
                      {!formData.allow_participant_posts && (
                        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                          ⚠️ This setting requires "Allow Participant Posts" to be enabled
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allow_participant_organizers}
                      onChange={(e) => handleChange('allow_participant_organizers', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Allow Participant Organizers
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Members can create their own asset organizers and folders within the bubble. This gives them more autonomy in organizing shared resources.
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>

            {/* ✅ NEW: Permission Summary Alert */}
            <Alert 
              severity={
                !formData.participant_visibility && formData.require_visibility_to_post 
                  ? "warning" 
                  : "info"
              }
              icon={
                !formData.participant_visibility && formData.require_visibility_to_post 
                  ? <WarningIcon /> 
                  : <SecurityIcon />
              }
            >
              <Typography variant="caption" component="div">
                <strong>Current Configuration:</strong>
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                • Members {formData.participant_visibility ? 'CAN' : 'CANNOT'} see each other
              </Typography>
              <Typography variant="caption" component="div">
                • Members {formData.allow_participant_posts ? 'CAN' : 'CANNOT'} create posts
              </Typography>
              {formData.allow_participant_posts && (
                <Typography variant="caption" component="div">
                  • {formData.require_visibility_to_post 
                      ? 'Only visible members can post' 
                      : 'All members can post (visible or hidden)'}
                </Typography>
              )}
              <Typography variant="caption" component="div">
                • Members {formData.allow_participant_organizers ? 'CAN' : 'CANNOT'} create organizers
              </Typography>
              
              {!formData.participant_visibility && formData.require_visibility_to_post && (
                <Typography variant="caption" component="div" color="warning.main" sx={{ mt: 1, fontWeight: 600 }}>
                  ⚠️ Warning: Visibility is disabled but posting requires visibility. Only admins will be able to post.
                </Typography>
              )}
            </Alert>

            <Alert severity="warning">
              <Typography variant="caption">
                <strong>Note:</strong> Changes to permissions apply immediately to all current and future members.
              </Typography>
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={updateSettingsMutation.isLoading || !formData.name.trim()}
        >
          {updateSettingsMutation.isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}